import branch from 'branch-sdk';
import ReactFacebookPixel from 'react-facebook-pixel';

import { updateCookieSettings as _updateCookieSettings, getCookieSettings, shouldDisableTracking } from '@pray/shared/utils/gdpr';

// The JS source defaults params to null, so TS infers them as null.
// Cast to accept booleans which is how all callers use it.
const updateCookieSettings = _updateCookieSettings as (settings: Partial<CookieSettings>) => CookieSettings;

import eventTracking from './eventTracking';
import initialize from './initialize';

type CookieSettings = {
  isBranchOptOut: boolean;
  isFacebookOptOut: boolean;
  isGoogleOptOut: boolean;
  isLiveConnectOptOut: boolean;
  isHotjarOptOut: boolean;
  isDatadogOptOut: boolean;
};

// Define the type for the context parameter
export type GoogleAnalyticsHandler = {
  disableGoogleAnalytics: (disable: boolean) => void;
};

/**
 * Toggle Branch.io tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleBranchTracking(optOut: boolean): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isBranchOptOut: shouldOptOut });
  branch.disableTracking(shouldOptOut);
  return updatedCookieSettings;
}

/**
 * Toggle Facebook Pixel tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleFacebookTracking(optOut: boolean): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isFacebookOptOut: shouldOptOut });

  if (shouldOptOut) {
    ReactFacebookPixel.revokeConsent();
    // Facebook sometimes leaves behind cookies that we should clean up
    clearAllLocalCookies();
  } else {
    ReactFacebookPixel.grantConsent();
  }

  return updatedCookieSettings;
}

/**
 * Toggle Google Analytics tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @param {GoogleAnalyticsHandler} handler - Object with disableGoogleAnalytics method
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleGoogleTracking(optOut: boolean, handler?: GoogleAnalyticsHandler): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isGoogleOptOut: shouldOptOut });

  // Disable Google Analytics if handler is provided
  if (handler?.disableGoogleAnalytics) {
    handler.disableGoogleAnalytics(shouldOptOut);
  }

  if (shouldOptOut) {
    // Google sometimes leaves behind cookies that we should clean up
    clearAllLocalCookies();
  }

  return updatedCookieSettings;
}

/**
 * Toggle LiveConnect tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleLiveConnectTracking(optOut: boolean): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isLiveConnectOptOut: shouldOptOut });

  if (shouldOptOut) {
    // LiveConnect sometimes leaves behind cookies that we should clean up
    clearAllLocalCookies();
  }

  return updatedCookieSettings;
}

/**
 * Toggle Hotjar tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleHotjarTracking(optOut: boolean): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isHotjarOptOut: shouldOptOut });

  if (shouldOptOut) {
    // Hotjar sometimes leaves behind cookies that we should clean up
    clearAllLocalCookies();
  }

  return updatedCookieSettings;
}

/**
 * Toggle DataDog tracking
 * @param {boolean} optOut - Whether to opt out (true) or opt in (false)
 * @returns {CookieSettings} Updated cookie settings
 */
export function toggleDatadogTracking(optOut: boolean): CookieSettings {
  // If DNT is enabled, force opt out
  const shouldOptOut = optOut || shouldDisableTracking();

  const updatedCookieSettings = updateCookieSettings({ isDatadogOptOut: shouldOptOut });

  if (shouldOptOut) {
    // DataDog sometimes leaves behind cookies that we should clean up
    clearAllLocalCookies();
  }

  return updatedCookieSettings;
}

/**
 * Opt out of all tracking services
 * @param {GoogleAnalyticsHandler} handler - Object with disableGoogleAnalytics method
 * @returns {CookieSettings} Updated cookie settings
 */
export function optOutOfAllCookies(handler?: GoogleAnalyticsHandler): CookieSettings {
  // Toggle each service individually
  toggleBranchTracking(true);
  toggleFacebookTracking(true);
  toggleGoogleTracking(true, handler);
  toggleLiveConnectTracking(true);
  toggleHotjarTracking(true);
  toggleDatadogTracking(true);

  // Return the final cookie settings
  return getCookieSettings();
}

/**
 * Opt in to all tracking services
 * @param {GoogleAnalyticsHandler} handler - Object with disableGoogleAnalytics method
 * @returns {CookieSettings} Updated cookie settings
 */
export function optInToAllCookies(handler?: GoogleAnalyticsHandler): CookieSettings {
  // Check if DNT is enabled - if so, don't opt in
  if (shouldDisableTracking()) {
    console.log('Cannot opt in to tracking due to DNT flag or user preferences');
    return getCookieSettings();
  }

  // Toggle each service individually
  toggleBranchTracking(false);
  toggleFacebookTracking(false);
  toggleGoogleTracking(false, handler);
  toggleLiveConnectTracking(false);
  toggleHotjarTracking(false);
  toggleDatadogTracking(false);

  // Return the final cookie settings
  return getCookieSettings();
}

/**
 * Check if user has opted out of all tracking
 * @returns {boolean} True if all tracking is opted out, false otherwise
 */
export function isAllTrackingOptedOut(): boolean {
  const cookieSettings = getCookieSettings();

  return (
    cookieSettings.isBranchOptOut &&
    cookieSettings.isFacebookOptOut &&
    cookieSettings.isGoogleOptOut &&
    cookieSettings.isLiveConnectOptOut &&
    cookieSettings.isHotjarOptOut &&
    cookieSettings.isDatadogOptOut
  );
}

/**
 * Helper function to clear all local cookies
 */
export function clearAllLocalCookies(): void {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const positionOfEquals = cookie.indexOf('=');
    const name = positionOfEquals > -1 ? cookie.substr(0, positionOfEquals) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

export async function handleCookieBannerAccepted() {
  // Determine app name based on current location
  const appName = window.location.hostname.includes('studio') ? 'studio' : 'www';

  // Initialize tracking libraries
  await initialize.initialize({ appName });

  // Send initial route event to all tracking platforms after initialization
  eventTracking.trackEventRoute({
    name: window.location.pathname,
  });
}
